import * as React from "react";

import { cn } from "../../lib/utils";
import { cva, type VariantProps } from "class-variance-authority";

const cardVariants = cva(
  "jobsite:bg-card jobsite:text-card-foreground jobsite:flex jobsite:flex-col jobsite:gap-6 jobsite:border-2 jobsite:border-border jobsite:py-6",
  {
    variants: {
      variant: {
        default: "",
        rounded: "jobsite:rounded-xl",
      },
    },
  },
);

function Card({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof cardVariants>) {
  return (
    <div
      data-slot="card"
      className={cn(cardVariants({ variant, className }))}
      style={{ borderRadius: "var(--jobsite-radius)" }}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "jobsite:@container/card-header jobsite:grid jobsite:auto-rows-min jobsite:grid-rows-[auto_auto] jobsite:items-start jobsite:gap-2 jobsite:px-6 jobsite:has-data-[slot=card-action]:grid-cols-[1fr_auto] jobsite:[.border-b]:pb-6",
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("jobsite:leading-none jobsite:font-semibold", className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("jobsite:text-muted-foreground jobsite:text-sm", className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "jobsite:col-start-2 jobsite:row-span-2 jobsite:row-start-1 jobsite:self-start jobsite:justify-self-end",
        className,
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-content" className={cn("jobsite:px-6", className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        "jobsite:flex jobsite:items-center jobsite:px-6 jobsite:[.border-t]:pt-6",
        className,
      )}
      {...props}
    />
  );
}

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent };
